import {cloneElement, forwardRef} from 'react'
import utils from '@eitje/utils'
import {Icon} from '../../icon'
import {t} from '@eitje/bridge'
import {Layout} from '../../layout'
import {Text} from '../../text'
import {Select} from '../../select'
import {useFilterContext} from '../context'
import {useFilterCache, useSetAndCacheFilter} from '../hooks'
import _ from 'lodash'

const CLEAR_ALL = {
	showAtCount: 1,
	t: 'filters.clear_filter',
}

export const Filter = ({className, ...props}) => {
	let {filterName, labelField, items, groupProps, ItemComponent} = props
	const {name, orgCacheKey} = useFilterContext()
	const setAndCacheFilter = useSetAndCacheFilter({orgCacheKey, name, filterName})
	const onChange = filterValue => setAndCacheFilter(filterValue)
	const filterValue = useFilterCache({filterName})
	const active = !_.isEmpty(filterValue)

	// Use labelT for simple string items.
	// To skip this default simple translation for string values, pass an object with a t or a labelField in the taxonomy if the item contains a database record.
	items = items.map(item => {
		if (_.isString(item)) return {id: item, t: `filters.filter.${filterName}.${item}`}
		return item
	})

	const triggerProps = {...props, filterValue, active, items}
	let trigger = <DefaultTrigger {...triggerProps} />
	if (props.trigger) trigger = cloneElement(props.trigger, {children: getTriggerLabel(triggerProps)})

	return (
		<Select
			className={className}
			trigger={trigger}
			type="multiple"
			items={items}
			value={filterValue}
			onChange={onChange}
			labelField={labelField}
			showClearAll={CLEAR_ALL}
			groupProps={groupProps}
			ItemComponent={ItemComponent}
		/>
	)
}

const getTriggerLabel = ({filterValue, transKey, labelField, items}) => {
	const valueAmountSelected = filterValue.length
	let label

	switch (valueAmountSelected) {
		case 0:
			label = t(transKey)
			break

		case 1: {
			const onlyValue = filterValue[0]
			const activeItem = items.flatMap(i => (i.subItems ? i.subItems : i)).find(item => item.id == onlyValue)
			if (labelField) {
				const recordName = activeItem?.[labelField]
				if (recordName === undefined) console.error('Filter error: could not find currently active recordName!')
				label = recordName
			} else {
				label = t(activeItem?.t)
			}
			break
		}
		default:
			label = `${t(transKey)} (${valueAmountSelected})`
	}

	return label
}

const DefaultTrigger = forwardRef(({filterName, filterValue, active, transKey, labelField, items, ...rest}, ref) => {
	const classNames = utils.makeCns('filter', `${_.kebabCase(filterName)}-filter`)
	const label = getTriggerLabel({filterValue, transKey, labelField, items})

	return (
		<Layout
			ref={ref}
			className={classNames}
			data-intercom-target={`${filterName}_filter`}
			height={24}
			padding="6 6 6 8"
			colorSet="initial-transparent"
			baseColor="blue"
			active={active}
			hasHover
			border
			borderRadius
			{...rest}
		>
			<Text small semiBold noTextSelect>
				{label}
			</Text>
			<Icon name="chevron-small-down" size={12} />
		</Layout>
	)
})
